import { Select, MenuItem, Checkbox, Box, Typography, useTheme, TextField, InputAdornment, Button, ListSubheader, LinearProgress } from '@mui/material'
import React, { forwardRef, useState, useMemo, useEffect } from 'react'
import { Icons, isReactComponent } from '../../utils/utilities'
import DataLoadingSpinner from './DataLoadingSpinner'


/**
 * @file CustomSelectField.jsx
 * @description A highly customized select input built on top of Material-UI's Select component,
 * featuring integrated text search filtering, support for grouped/categorized data structures, 
 * customizable action headers, multiselect optimization with structural checkboxes, and sticky controls.
 */

/**
 * @param {Object} props
 *
 * // Core
 * @param {*} props.value
 * @param {function(import('@mui/material').SelectChangeEvent<any>, React.ReactNode): void} props.onChange
 * @param {Array<any>|Object<string, Array<any>>} [props.data=[]]
 * @param {boolean} [props.multiple=false]
 * @param {string} [props.id]
 * @param {string} [props.placeholder="Select an option"]
 *
 * // Rendering
 * @param {(item:any, index:number)=>React.ReactNode} [props.renderOptionText]
 * @param {(selected:any)=>React.ReactNode} [props.renderValue]
 * @param {(item:any, search:string)=>boolean} [props.searchFilter]
 *
 * // Item configuration
 * @param {string} [props.itemValueKey]
 * @param {string} [props.itemIdentityKey]
 * @param {string} [props.noOptionsMessage]
 * @param {string} [props.optionsMaxHeight="350px"]
 * @param {number} [props.searchRenderThreshold=Infinity]
 * @param {number} [props.minSearchLength=1]
 *
 * // Styles
 * @param {import('@mui/material').SxProps} [props.selectStyles]
 * @param {import('@mui/material').SxProps} [props.menuTextStyles]
 * @param {import('@mui/material').SxProps} [props.menuItemStyles]
 * @param {import('@mui/material').MenuProps} [props.selectMenuProps]
 *
 * // Header Actions
 * @param {Array<React.ReactNode|Object>} [props.customStartActions=[]]
 *
 * // Options
 * @param {Object} [props.options]
 * @param {boolean} [props.options.showCheckbox=false]
 * @param {boolean} [props.options.showCheckIconAtEnd=true]
 */
const CustomSelectField = forwardRef( ( {
    id,
    value,
    onChange,
    data = [],
    placeholder = "Select an option",
    renderOptionText,
    selectStyles = {},
    selectMenuProps = {},
    menuTextStyles = {},
    menuItemStyles = {},
    itemValueKey,
    itemIdentityKey,
    renderValue,
    searchFilter,
    onSearch,
    noOptionsMessage,
    customStartActions = [],
    optionsMaxHeight = "250px",
    searchPlaceholder = "Search in options...",
    options = {
        showCheckbox: false,
        showCheckIconAtEnd: true,
    },
    searchRenderThreshold = 100,
    minSearchLength = 1,
    enableAddNew = false,
    addingNew = false,
    handleAddNew,
    loadingOptions = false,
    loadinOptionsMessage = "Loading options...",
    ...props
}, ref ) => {
    const { showCheckbox = false, showCheckIconAtEnd = true } = options

    const [searchTerm, setSearchTerm] = useState( "" )
    const [open, setOpen] = useState( false )

    const { palette, border, theme } = useTheme()


    const totalOptions = Array.isArray( data )
        ? data.length
        : Object.values( data ).flat().length

    const onSearchExists = onSearch && typeof onSearch === "function"
    const shouldDelayRendering =
        ( searchFilter &&
            totalOptions >= searchRenderThreshold &&
            searchTerm.length < minSearchLength )


    const onSearchChange = ( event ) => {
        if ( onSearch && typeof onSearch === 'function' ) {
            onSearch( event.target.value )
            setSearchTerm( event.target.value )
        } else {
            setSearchTerm( event.target.value )
        }
    }

    const getLabel = ( item, index ) => {
        if ( !item ) return ""
        if ( renderOptionText ) return renderOptionText( item, index )
        return (
            <Typography
                sx={{
                    fontSize: "14px",
                    ...menuTextStyles
                }}>
                {typeof item === 'object' ? ( item.label || item.name || "" ) : item}
            </Typography>
        )
    }

    const filteredData = useMemo( () => {
        if ( shouldDelayRendering )
            return []

        if ( Array.isArray( data ) ) {
            if ( onSearchExists || !searchFilter || !searchTerm ) return data
            return data.filter( item => searchFilter( item, searchTerm ) )
        } else if ( typeof data === 'object' ) {
            const filteredDataList = []
            for ( const category in data ) {
                if ( !Object.hasOwn( data, category ) ) continue
                let items = []
                if ( !searchFilter || !searchTerm )
                    items = data[category]
                else
                    items = data[category].filter( item => searchFilter( item, searchTerm ) )
                if ( items.length > 0 ) {
                    filteredDataList.push( { menuItemType: "category-label", label: category, value: category } )
                    for ( const item of items ) {
                        item.menuItemType = "list-item"
                        filteredDataList.push( item )
                    }
                }
            }
            return filteredDataList
        }
    }, [data, searchTerm, searchFilter, shouldDelayRendering, onSearchExists] )

    const handleCloseInternal = ( e ) => {
        setOpen( false )
    }

    return (
        <Select
            ref={ref}
            displayEmpty
            value={value || ( props.multiple ? [] : "" )}
            onChange={onChange}
            onClick={e => e.stopPropagation()}
            onClose={() => setOpen( false )}
            onOpen={() => setOpen( true )}
            open={open}
            {...props}
            MenuProps={{
                transitionDuration: 0,
                disablePortal: selectMenuProps?.disablePortal ?? false,
                autoFocus: false,
                variant: "selectedMenu",
                onTransitionEnter: () => {
                    const searchField = document.getElementById( "custom-select-search-field" )
                    if ( searchField )
                        setTimeout( () => searchField?.focus(), 100 )
                },
                anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left"
                },
                transformOrigin: {
                    vertical: "top",
                    horizontal: "left"
                },
                slotProps: {
                    paper: {
                        className: "slim-custom-scrollbar",
                        sx: { marginTop: "5px", borderRadius: "5px", border: border[1], boxShadow: `0px 1px 4px 1px rgba(0,0,0,0.1)`, maxHeight: optionsMaxHeight }
                    }
                },
                ...selectMenuProps,
            }}
            sx={{
                width: "100%",
                textTransform: "capitalize",
                color: ( !value || ( Array.isArray( value ) && value.length === 0 ) )
                    ? ( selectStyles.placeholderColor || palette.form?.placeholder || palette.text.secondary )
                    : palette.text.primary,
                ":focus-within": {
                    outline: `1px solid ${palette.primary.main}`
                },
                ...selectStyles,
            }}
            renderValue={
                ( renderValue && typeof renderValue === 'function' ) ?
                    ( selected ) => renderValue( selected, data )
                    : ( selected ) => {
                        if ( !String( selected ).trim() || ( Array.isArray( selected ) && selected.length === 0 ) ) {
                            return <span style={{ color: palette.form?.placeholder || '#aaa' }}>{placeholder}</span>
                        }
                        if ( Array.isArray( selected ) ) {

                            return <Box sx={{ display: "flex", gap: 1 }} >
                                {selected.map( val => {
                                    const item = data.find( d => ( itemValueKey ? d[itemValueKey] : d.value ?? d ) === val )
                                    return getLabel( item || val )
                                } )}
                            </Box>
                        }
                        const selectedItem = data.find( item => ( itemValueKey ? item[itemValueKey] : item.value ?? item ) === selected )
                        return getLabel( selectedItem || selected )
                    }
            }
            labelId={id}
        >
            {/* --- Sticky Header (Search) --- */}
            {( onSearch || searchFilter || customStartActions?.length > 0 ) && (
                <Box
                    onKeyDown={( e ) => e.stopPropagation()}
                    sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}
                >
                    {( searchFilter || onSearch ) && <TextField
                        autoComplete='off'
                        size="small"
                        fullWidth
                        placeholder={searchPlaceholder || "Search"}
                        variant='standard'
                        id="custom-select-search-field"
                        value={searchTerm}
                        onChange={onSearchChange}
                        onKeyDown={( e ) => {
                            if ( e.key !== 'Escape' ) e.stopPropagation()
                        }}
                        onClick={( e ) => {
                            e.stopPropagation()
                        }}
                        slotProps={{
                            input: {
                                sx: {
                                    padding: "10px",
                                    borderBottom: border[1],
                                    fontSize: "14px"
                                },
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {Icons.default.SearchIcon}
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />}

                    {customStartActions?.length > 0 && <Box
                        sx={{
                            padding: "5px 10px",
                            borderBottom: border[1],
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap"
                        }}>
                        {customStartActions.map( ( item, idx ) => {
                            if ( isReactComponent( item ) )
                                return <Box component="span" key={`csf-start-action-${idx}`}>
                                    {item}
                                </Box>
                            if ( typeof item === "object" && item?.type === 'button' && typeof item.onClick === 'function' && item.label ) {
                                return ( <Button sx={item.styles || {}} key={`csf-start-action-${idx}`} size={item?.size || "small"} startIcon={item?.startIcon} onClick={item.onClick} endIcon={item?.endIcon} >
                                    {item.label}
                                </Button> )
                            }
                            return null
                        } )}
                    </Box>}

                </Box>
            )}
            {loadingOptions && <Box sx={{ pointerEvents: "none", width: "100%" }}>
                <LinearProgress color="info" />
            </Box>}
            {/* --- List Items --- */}
            {Array.isArray( filteredData ) && filteredData.length > 0 && (
                filteredData.map( ( item, index ) => {

                    if ( item.menuItemType === 'category-label' ) {

                        return ( <ListSubheader key={`header-${item.label}`} sx={{ fontWeight: 'bold', bgcolor: palette.contentBg, lineHeight: '32px' }}>
                            {item.label}
                        </ListSubheader> )
                    }

                    const itemValue = itemValueKey ? item[itemValueKey] : item.value !== undefined ? item.value : item
                    const key = ( itemIdentityKey || itemValueKey ) ? item[itemIdentityKey || itemValueKey] : item.value !== undefined ? item.value : item
                    const isSelected = Array.isArray( value )
                        ? value.map( v => String( v ) ).includes( String( itemValue ) )
                        : String( value ) === String( itemValue )

                    return (
                        <MenuItem
                            disabled={loadingOptions}
                            autoFocus={isSelected}
                            id={itemValue}
                            dense
                            key={key || index}
                            value={itemValue}
                            sx={{ fontSize: "14px", justifyContent: "space-between", ...menuItemStyles }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {showCheckbox && (
                                    <Checkbox size="small" color={theme === 'dark' ? "greyed" : "customThemeColor"} checked={isSelected} sx={{ p: 0 }} />
                                )}
                                {getLabel( item, index )}
                            </Box>
                            {showCheckIconAtEnd && isSelected && (
                                Icons.default.CheckIcon
                            )}
                        </MenuItem>
                    )
                } )
            )}
            {!loadingOptions && ( shouldDelayRendering || ( onSearchExists && searchTerm.length < minSearchLength ) ) && (
                <Box sx={{ p: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            textAlign: "center",
                            display: "block"
                        }}>
                        {!onSearchExists ? `Type at least ${minSearchLength} character${minSearchLength > 1 ? "s" : ""} to search from ${totalOptions} options.` : `Type at least ${minSearchLength} character${minSearchLength > 1 ? "s" : ""} to search.`}
                    </Typography>
                </Box>
            )}
            {!loadingOptions && Array.isArray( filteredData ) && filteredData.length === 0 && !( shouldDelayRendering || ( onSearchExists && searchTerm.length < minSearchLength ) ) && (
                <Box
                    sx={{
                        p: 2,
                        textAlign: "center",
                        pointerEvents: "none",
                        display: "flex", flexDirection: "column",
                        gap: 1,
                        alignItems: "center"
                    }}>
                    <Typography variant="caption" color="textSecondary" sx={{
                        textAlign: "center"
                    }}>{noOptionsMessage || "No results found"}</Typography>

                    {( enableAddNew && handleAddNew && typeof handleAddNew === 'function' ) && searchTerm?.trim().length > 0 && <Button
                        disableElevation
                        variant="outlined"
                        size="small"
                        color='greyed'
                        loading={addingNew}
                        startIcon={Icons.default.AddBox}
                        onClick={event => handleAddNew( event, searchTerm, filteredData )}
                        onKeyDown={( e ) => e.stopPropagation()}
                        sx={{ pointerEvents: "all", textTransform: 'none', fontSize: '12px' }}
                    >
                        <Typography sx={{ fontSize: "inherit" }} component="span">Add <Typography sx={{ fontSize: "inherit", textDecoration: "underline", fontWeight: 600 }} component="em">{searchTerm}</Typography> </Typography>
                    </Button>}
                </Box>
            )}
            {/* --- Sticky Footer (Done Button) --- */}
            {( props.multiple || ( enableAddNew && handleAddNew && typeof handleAddNew === 'function' ) ) && (
                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    borderTop: border[1],
                    p: 1,
                    gap: 1,
                    display: 'flex',
                    justifyContent: 'flex-start'
                }}>
                    {props.multiple && <Button
                        disableElevation
                        variant="contained"
                        size="small"
                        onClick={handleCloseInternal}
                        onKeyDown={( e ) => e.stopPropagation()}
                        sx={{ textTransform: 'none', fontSize: '12px' }}
                    >
                        Okay
                    </Button>}
                    {enableAddNew && <Button
                        disableElevation
                        variant="outlined"
                        size="small"
                        color='greyed'
                        loading={addingNew}
                        startIcon={Icons.default.AddBox}
                        onClick={event => handleAddNew( event, searchTerm, filteredData )}
                        onKeyDown={( e ) => e.stopPropagation()}
                        sx={{ textTransform: 'none', fontSize: '12px' }}
                    >
                        <Typography sx={{ fontSize: "inherit" }} component="span">Add <Typography sx={{ fontSize: "inherit", textDecoration: "underline", fontWeight: 600 }} component="em">{searchTerm}</Typography> </Typography>
                    </Button>}
                </Box>
            )}
        </Select>
    )
} )

CustomSelectField.displayName = 'CustomSelectField'
export default CustomSelectField